/**
 * How a credit count is written, everywhere a reader meets one.
 *
 * The editor wrote "15 cr" in one panel and "15 credits" in the next, and a
 * one-credit module is ordinary in FET provision, so a hand-spelled plural
 * read "1 credits" wherever the count happened to be one.
 *
 * @module components/shared/credits
 */

import { pluralNoun } from "@/components/ui";

/** A stored credit value, which older records hold as a string. */
type CreditValue = number | string | null | undefined;

function count(credits: CreditValue): number {
  const value = Number(credits ?? 0);
  return Number.isFinite(value) ? value : 0;
}

/** The noun alone, for a value drawn beside it: "credit" or "credits". */
export function creditsUnit(credits: CreditValue): string {
  return pluralNoun(count(credits), "credit");
}

/** The count and its noun: "1 credit", "10 credits". */
export function creditsLabel(credits: CreditValue): string {
  return `${count(credits)} ${creditsUnit(credits)}`;
}

/**
 * The count and its noun, or `missing` when none is recorded. Stored credits
 * default to zero, so zero means nobody has entered them yet.
 */
export function recordedCreditsLabel(credits: CreditValue, missing = "Credits not set"): string {
  return count(credits) > 0 ? creditsLabel(credits) : missing;
}

/** A bare count for a table cell headed "Credits", or a dash where none is recorded. */
export function recordedCredits(credits: CreditValue): number | string {
  return count(credits) > 0 ? count(credits) : "—";
}
